//

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useRef, useState } from "react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useAuth } from "@/context/AuthContext";
import {
  Building,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  MapPin,
  Rocket,
  User,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import arrow from "../../assets/images/arrow.webp";
import { useAppContext } from "../../context/AppContext";

import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { leadBatchApi } from "../../Redux/ReduxSlices/leadBatchSlice";
import {
  parseIntent,
  searchPeople,
} from "../../Redux/ReduxSlices/newAppoloApiSlice";
import { fetchPeople } from "../../Redux/ReduxSlices/newsearchSlice";
import {
  apolopeoplePerPage,
  peoplepages,
  peoplePerPage,
} from "../../utils/perPageValue";
import AuthModal from "../auth/AuthModal";
import defaultProfile from "../../assets/images/default_profile.webp";

const blockedDomains = new Set();
const PREVIEW_TIMEOUT_MS = 800;
const PROXY_TIMEOUT_MS = 1800;

// 🔹 One place to control page size
const PAGE_SIZE = 25;

const WebsiteHoverPreview = ({ url, children }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("loading");
  const [attempt, setAttempt] = useState("direct");
  const [src, setSrc] = useState("");

  useEffect(() => {
    if (!open) {
      setMode("loading");
      setAttempt("direct");
      setSrc("");
      return;
    }

    if (hostname && blockedDomains.has(hostname)) {
      setAttempt("proxy");
      setSrc(proxyUrl);
      setMode("loading");
      return;
    }

    setAttempt("direct");
    setSrc(normalized);
    setMode("loading");

    const to = setTimeout(() => {
      if (attempt === "direct" && mode === "loading") {
        setAttempt("proxy");
        setSrc(proxyUrl);
        setMode("loading");
      }
    }, PREVIEW_TIMEOUT_MS);

    return () => clearTimeout(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hostname, url]);

  useEffect(() => {
    if (!open) return;
    if (attempt !== "proxy" || mode !== "loading") return;

    const to = setTimeout(() => {
      if (mode === "loading") {
        setMode("fallback");
        if (hostname) blockedDomains.add(hostname);
      }
    }, PROXY_TIMEOUT_MS);
    return () => clearTimeout(to);
  }, [attempt, mode, open, hostname]);

  const handleFrameLoad = () => {
    setMode("frame");
  };

  return (
    <HoverCard
      openDelay={80}
      closeDelay={100}
      open={open}
      onOpenChange={setOpen}
    >
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent className="w-[420px] p-0">
        {mode === "frame" ? (
          <div className="relative h-[260px] w-full bg-muted">
            <iframe
              src={src}
              className="h-full w-full rounded"
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              referrerPolicy="no-referrer"
              onLoad={handleFrameLoad}
            />
          </div>
        ) : mode === "loading" ? (
          <div className="relative h-[180px] w-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {attempt === "direct"
              ? "Loading preview..."
              : "Loading via proxy..."}
          </div>
        ) : (
          <div className="p-3 flex items-center space-x-3">
            {favicon && (
              <img src={favicon} alt="" className="h-6 w-6 rounded" />
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium truncate max-w-[320px]">
                {hostname || normalized}
              </div>
              <div className="text-xs text-muted-foreground truncate max-w-[320px]">
                Preview blocked by site (X-Frame-Options/CSP). Click Open to
                view in a new tab.
              </div>
            </div>
          </div>
        )}
        <div className="border-t px-3 py-2 text-xs flex items-center justify-between">
          <span className="truncate max-w-[300px]">
            {attempt === "proxy" ? proxyUrl : normalized}
          </span>
          <a
            href={normalized}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
          >
            <span>Open</span>
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const PromptData = () => {
  const {
    promptsDataCompany,
    setViewMode,
    promptsDataPeople,
    viewMode,
    setPromptsDataPeople,
    setShowPromtData,
    setShowUrlData,
    prompt,
    promptInput,
    isSearchLoading,
    setIsSearchLoading,
    loadingMessage,
    setLoadingMessage,
    defaultLocation,
    getContactsCount,
  } = useAppContext();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState("login");
  const [showWaitingListModal, setShowWaitingListModal] = useState(false);
  const { details: userData } = useSelector((state) => state.profile);
  // Pagination state
  const [companyPage, setCompanyPage] = useState(1);
  const [peoplePage, setPeoplePage] = useState(1);
  const [isLaunching, setIsLaunching] = useState(false);
  const [contactsCount, setContactsCount] = useState(0);

  // Track previous values
  const prevRef = useRef({
    promptInput: null,
    data: null,
  });

  useEffect(() => {
    const prev = prevRef.current;

    const promptChanged = prev.promptInput !== promptInput;
    const dataChanged = prev.data !== promptsDataPeople;

    // ✅ ONLY update when BOTH changed
    if (promptChanged && dataChanged) {
      const combinedKey = `${promptInput}`;
      const value = getContactsCount(combinedKey);
      setContactsCount(value);
    }

    // update previous values
    prevRef.current = {
      promptInput,
      data: promptsDataPeople,
    };
  }, [promptInput, promptsDataPeople]);

  const openAuthModal = (tab) => {
    setAuthModalTab(tab);
    setAuthModalOpen(true);
  };

  const comapniesList =
    promptsDataCompany?.results?.organizations || promptsDataCompany || [];

  const rawPeopleList =
    promptsDataPeople?.results?.people ||
    promptsDataPeople?.people ||
    promptsDataPeople?.data ||
    promptsDataPeople?.data?.people ||
    promptsDataPeople ||
    [];
  const peopleList = Array.isArray(rawPeopleList) ? rawPeopleList : [];

  const getTotalPeopleCount = () => {
    return peopleList?.length || 0;
  };

  const getFullName = (p) => {
    if (p?.full_name?.trim()) {
      return p.full_name.trim() || null;
    }

    const first = p?.first_name?.trim() || null;

    const combined = `${first}`.trim();
    return combined || null;
  };

  const buildItemsBySource = (source, data = []) => {
    if (!Array.isArray(data)) return [];

    if (source === "db") {
      return data
        .filter((p) => typeof p?.email === "string" && p.email.trim() !== "")
        .map((p) => ({
          item: p.email, // email id
          name: getFullName(p), // full name
        }));
    }

    if (source === "api") {
      return data
        .filter((p) => p?.id) // ✅ filter by id, not email
        .map((p) => ({
          item: p.id, // ✅ API item = id
          name: getFullName(p), // ✅ full name
        }));
    }

    return [];
  };

  const handleLaunchCampaign = async () => {
    try {
      setIsLaunching(true);
      const userId = userData?.data?.id;
      // const allowedUserIds = ALLOWED_USER_IDS;

      // if (!allowedUserIds.includes(userId)) {
      //   setShowWaitingListModal(true);
      //   return;
      // }
      if (!isAuthenticated) {
        openAuthModal("signup");
        return;
      }

      const sessionId = localStorage.getItem("sessionId");

      if (!sessionId) {
        toast.error("Session not found. Please run the prompt again.");
        return;
      }

      const source = promptsDataPeople?.source;
      if (!source) {
        toast.error("Unable to detect data source.");
        return;
      }

      const data =
        (Array.isArray(promptsDataPeople?.data) && promptsDataPeople.data) ||
        (Array.isArray(promptsDataPeople?.results?.people) &&
          promptsDataPeople.results.people) ||
        (Array.isArray(promptsDataPeople?.people) &&
          promptsDataPeople.people) ||
        peopleList ||
        [];

      const leads = buildItemsBySource(source, data);

      const payload = {
        user_id: userId || sessionId,
        session_id: sessionId,
        prompt: promptInput,
        source,
        items: leads.map((l) => l.item),
        names: leads.map((l) => l.name),
      };
      // ✅ IMPORTANT: await unwrap
      await dispatch(leadBatchApi(payload)).unwrap();

      toast.success(
        `You're ready to launch your campaign. Connect your email or choose a connected account to continue`,
      );
      navigate("/send-emails");
    } catch (err) {
      // ✅ ALWAYS stringify error
      toast.error(err?.message || "Failed to launch campaign");
    } finally {
      setIsLaunching(false);
    }
  };

  const handleLoginRedirect = () => {
    setShowLoginDialog(false);
    navigate("/login");
  };

  // 🔹 Pagination logic (25 per page)
  const totalCompanies = comapniesList.length || 0;
  const totalCompanyPages = Math.max(1, Math.ceil(totalCompanies / PAGE_SIZE));

  const totalPeople = peopleList.length || 0;
  const totalPeoplePages = Math.max(1, Math.ceil(totalPeople / PAGE_SIZE));
  // const totalPeoplePages = Math.max(1, Math.ceil(contactsCount / PAGE_SIZE));

  const companyStartIndex =
    (companyPage - 1) * PAGE_SIZE >= totalCompanies
      ? 0
      : (companyPage - 1) * PAGE_SIZE;
  const companyEndIndex = Math.min(
    companyStartIndex + PAGE_SIZE,
    totalCompanies,
  );

  const displayedCompanies = comapniesList.slice(
    companyStartIndex,
    companyEndIndex,
  );

  const peopleStartIndex =
    (peoplePage - 1) * PAGE_SIZE >= totalPeople
      ? 0
      : (peoplePage - 1) * PAGE_SIZE;
  const peopleEndIndex = Math.min(peopleStartIndex + PAGE_SIZE, totalPeople);

  const displayedPeople = peopleList.slice(peopleStartIndex, peopleEndIndex);

  // Reset page when data or view changes
  useEffect(() => {
    setCompanyPage(1);
  }, [totalCompanies]);

  useEffect(() => {
    setPeoplePage(1);
  }, [totalPeople]);

  useEffect(() => {
    if (viewMode === "companies") {
      setCompanyPage(1);
    } else if (viewMode === "people") {
      setPeoplePage(1);
    }
  }, [viewMode]);

  const handleSearchPeople = async () => {
    const companies = comapniesList || [];
    if (companies.length === 0) {
      toast.error(
        "No companies available to fetch people from. Run the prompt again first.",
      );
      return;
    }

    setViewMode("people");
    setIsSearchLoading(true);

    try {
      const userId = localStorage.getItem("userId") || "default_user";
      const sessionId = localStorage.getItem("sessionId");

      if (!sessionId) {
        toast.error("No active session found. Please start a new session.");
        setIsSearchLoading(false);
        return;
      }

      const parsePayload = {
        user_id: userId,
        session_id: sessionId,
        prompt: promptInput,
        use_goapi: true,
      };

      const parseRes = await dispatch(parseIntent(parsePayload)).unwrap();
      const intent = parseRes?.intent || {};
      const peopleFilters = parseRes?.people_filters || {};
      const aiPropmt = parseRes.ai_prompt;
      const personTitles = peopleFilters?.person_titles?.length
        ? peopleFilters.person_titles
        : peopleFilters?.person_seniorities?.length
          ? peopleFilters.person_seniorities
          : ["manager", "ceo", "founder"];

      const personLocations = peopleFilters?.person_locations?.length
        ? peopleFilters.person_locations
        : intent?.locations || [];

      const industries = Array.isArray(intent?.industries)
        ? intent.industries
        : intent?.industries
          ? [intent.industries]
          : [];

      const jobTitle = personTitles;
      const location = personLocations?.[0] || "usa";

      const dbPayload = {
        job_title: jobTitle,
        location,
        industry: industries,
        limit: peoplePerPage,
        prompt: promptInput,
      };

      let dbPeople = [];

      // ============================
      // ⭐ DATABASE LOOKUP
      // ============================
      try {
        const dbResponse = await dispatch(fetchPeople(dbPayload)).unwrap();

        dbPeople =
          dbResponse?.data ||
          dbResponse?.results?.people ||
          dbResponse?.results ||
          [];

        // ⭐ NEW CONDITION: If DB < 20 → SearchPeople
        if (dbPeople.length < 30) {
          const searchPayload = {
            person_locations: personLocations.length
              ? personLocations
              : ["India", "United States"],
            person_titles: jobTitle,
            per_page: apolopeoplePerPage,
            include_similar_titles: true,
            pages: peoplepages,
            q_organization_keyword_tags: industries,
          };

          const searchRes = await dispatch(
            searchPeople(searchPayload),
          ).unwrap();

          const searchResults =
            searchRes?.people ||
            searchRes?.results?.people ||
            searchRes?.results ||
            [];

          setPromptsDataPeople({ source: "api", data: searchResults });
          setShowPromtData(true);
          setShowUrlData?.(false);

          toast.success(
            `Fetched ${searchResults.length} people from search (fallback)`,
          );

          setIsSearchLoading(false);
          return;
        }

        // If DB has >= 20 results → use DB
        if (dbPeople.length >= 20) {
          setPromptsDataPeople({ source: "db", data: dbPeople });
          setShowPromtData(true);
          setShowUrlData?.(false);

          toast.success(`Fetched ${dbPeople.length} people from database!`);
          setIsSearchLoading(false);
          return;
        }
      } catch (err) {
        console.error("fetchPeople failed → fallback triggered", err);
        toast.error("Database lookup failed.");
      }

      const searchPayload = {
        person_locations: personLocations.length ? personLocations : [],
        person_titles: jobTitle,
        per_page: apolopeoplePerPage,
        include_similar_titles: true,
        pages: peoplepages,
        q_organization_keyword_tags: industries,
      };

      const searchRes = await dispatch(searchPeople(searchPayload)).unwrap();

      const searchResults =
        searchRes?.people ||
        searchRes?.results?.people ||
        searchRes?.results ||
        [];

      if (searchResults.length > 0) {
        setPromptsDataPeople({ source: "api", data: searchResults });
        setShowPromtData(true);
        setShowUrlData?.(false);
        toast.success(`Fetched ${searchResults.length} people from search!`);
      } else {
        toast.error("No people found.");
      }
    } catch (err) {
      console.error("[handleSearchPeople] Unexpected error:", err);
      toast.error("Something went wrong while fetching people.");
    } finally {
      setIsSearchLoading(false);
    }
  };

  const toTitleCase = (str = "") =>
    str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  return (
    <div className="">
      {/*  Companies table */}
      {viewMode == "companies" && (
        <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/20  overflow-hidden max-md:mt-3">
          {/* Header */}
          <div className=" px-4 py-3 sm:px-6 mt-4 max-md:mt-0">
            <div className="flex  justify-between sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center max-md:justify-center space-x-3 ">
                <div className=" max-md:p-0 p-2 bg-blue-100 rounded-lg">
                  <Building className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg max-md:text-sm font-semibold text-gray-900">
                    Companies Found
                  </h2>
                  <p className="text-sm text-gray-600">
                    {totalCompanies} companies
                  </p>
                </div>
              </div>

              <div>
                <Button
                  type="button"
                  onClick={handleSearchPeople}
                  title={
                    !localStorage.getItem("sessionId")
                      ? "Start a session first (run a prompt) to enable"
                      : "Find people from these companies"
                  }
                  className="w-full sm:w-auto px-6 max-md:px-4 h-12 text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl rounded-lg transition-all duration-200 flex items-center gap-3"
                  size="default"
                >
                  {/* LEFT ICON */}
                  <Users className="h-5 w-5 max-md:h-8 max-md:w-8" />

                  {/* CENTER TEXT IN COLUMN */}
                  <div className="flex flex-col items-center leading-tight">
                    <span className="text-white text-sm font-semibold">
                      Next
                    </span>
                    <span className="text-gray-200 text-xs">Find People</span>
                  </div>
                </Button>
              </div>
            </div>
          </div>
          <div className="mb-2">
            <p className="flex items-center gap-2 text-[14px] bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent text-left font-semibold">
              <img src={arrow} alt="Swipe left" className="w-6 h-6 " />
              <span>Swipe left to see more content</span>
            </p>
          </div>
          {/* Table Container */}
          <div className="overflow-x-auto">
            {/* Header row */}
            <div className="bg-gray-50/80 backdrop-blur-sm px-3 py-2 border-b border-white/20 min-w-[600px]">
              <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-700 uppercase tracking-wider">
                <div className="col-span-4">Company</div>
                <div className="col-span-4">Company Website</div>
                <div className="col-span-4">Linkedin URL</div>
              </div>
            </div>

            {/* Body (scrollable) */}
            <div className="divide-y divide-gray-200/50 overflow-auto max-h-[60vh] min-h-[200px] min-w-[600px]">
              {totalCompanies === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-10 text-gray-600">
                  <svg
                    className="h-10 w-10 text-gray-400 mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <p className="font-medium text-sm sm:text-base text-gray-800">
                    No companies found for this prompt.
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Try using another prompt to discover more companies.
                  </p>
                </div>
              ) : (
                displayedCompanies
                  .filter((company) => {
                    const rawName =
                      company.name ||
                      company["Company Name"] ||
                      company.company_name ||
                      "";

                    const rawWebsite =
                      company.website_url ||
                      company.domain ||
                      company["Company Website"] ||
                      "";

                    return rawName.trim() !== "";
                  })
                  .map((company, index) => {
                    const rawCompanyName =
                      company.name ||
                      company["Company Name"] ||
                      company.company_name ||
                      "-";

                    const companyName = toTitleCase(rawCompanyName);

                    const rawWebsite =
                      company.website_url ||
                      company.domain ||
                      company["Company Website"] ||
                      "";

                    const website = rawWebsite;

                    const rawLinkedin =
                      company.linkedin_url ||
                      company.linkedin ||
                      company["LinkedIn Url"] ||
                      "";

                    const linkedin = rawLinkedin || "_ ";
                    const logo = company.logo_url || "";
                    const founded = company.founded_year;

                    return (
                      <div
                        key={company.id || index}
                        className="px-3 py-2.5 hover:bg-gray-50/50 transition-colors"
                      >
                        <div className="grid grid-cols-12 gap-3 items-center">
                          <div className="col-span-4">
                            <div className="flex items-center space-x-1.5">
                              {logo ? (
                                <img
                                  src={logo}
                                  alt={companyName}
                                  className="h-6 w-6 object-contain rounded"
                                />
                              ) : (
                                <Building className="h-4 w-4 text-gray-400" />
                              )}
                              <div>
                                <h3 className="font-medium text-base max-md:text-xs text-gray-900">
                                  {companyName}
                                </h3>
                                {founded && (
                                  <p className="text-xs text-gray-500">
                                    Founded {founded}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="col-span-4">
                            <HoverCard>
                              <HoverCardTrigger asChild>
                                {website ? (
                                  <a
                                    href={
                                      website.startsWith("http")
                                        ? website
                                        : `https://${website}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-base max-md:text-xs text-blue-600 hover:underline line-clamp-2 cursor-pointer"
                                  >
                                    {website}
                                  </a>
                                ) : (
                                  <p className="text-base max-md:text-xs text-gray-600">
                                    -
                                  </p>
                                )}
                              </HoverCardTrigger>
                              <HoverCardContent
                                side="top"
                                align="start"
                                className="max-w-sm text-xs leading-relaxed"
                              >
                                {website || "-"}
                              </HoverCardContent>
                            </HoverCard>
                          </div>

                          <div className="col-span-4">
                            <div className="flex items-center space-x-1">
                              {linkedin ? (
                                <a
                                  href={
                                    linkedin.startsWith("http")
                                      ? linkedin
                                      : `https://${linkedin}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-base max-md:text-xs text-blue-600 hover:underline"
                                >
                                  {linkedin}
                                </a>
                              ) : (
                                <span className="text-base max-md:text-xs text-gray-900">
                                  -
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>

            {/* Pagination footer */}
            {totalCompanies > 0 && (
              <div className="flex items-center justify-between px-4 py-2 border-t bg-white text-xs text-gray-700">
                <span>
                  Showing {totalCompanies === 0 ? 0 : companyStartIndex + 1}–
                  {companyEndIndex} of {totalCompanies}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={companyPage === 1}
                    onClick={() =>
                      setCompanyPage((prev) => Math.max(1, prev - 1))
                    }
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>
                    Page {companyPage} of {totalCompanyPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={companyPage === totalCompanyPages}
                    onClick={() =>
                      setCompanyPage((prev) =>
                        Math.min(totalCompanyPages, prev + 1),
                      )
                    }
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/*  People table */}
      {viewMode == "people" && (
        <>
          {/* Action bar */}
          <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/20  overflow-hidden mb-4 mt-2">
            <div className="bg-gradient-to-r from-gray-100 to-green-50/30 border-b border-gray-200/50 px-4 max-md:px-2 py-3 sm:px-6">
              <div className="flex   justify-between sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center max-md:justify-center space-x-3">
                  <div className="p-2 max-md:p-0 bg-blue-100 rounded-lg">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg max-md:text-xs font-semibold text-gray-900">
                      People Found
                    </h2>
                    <p className="text-sm text-gray-600">
                      <b>
                        {getTotalPeopleCount().toLocaleString("en-IN")} contacts
                      </b>
                    </p>
                  </div>
                </div>

                {/* <Button
                  onClick={handleLaunchCampaign}
                  className="w-1/2 sm:w-auto px-6 max-md:px-0 h-10 text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                  size="default"
                  disabled={getTotalPeopleCount() === 0}
                >
                  <Rocket className="h-4 w-4" />
                  <span>Send Emails ({getTotalPeopleCount()})</span>
                </Button> */}

                <Button
                  onClick={handleLaunchCampaign}
                  disabled={getTotalPeopleCount() === 0 || isLaunching}
                  className="w-1/2 sm:w-auto px-6 max-md:px-0 h-10 text-xs font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  {isLaunching ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Launching…</span>
                    </>
                  ) : (
                    <>
                      <Rocket className="h-4 w-4" />
                      <span>Send Emails ({getTotalPeopleCount()})</span>
                    </>
                  )}
                </Button>

                {/* <Button
                  onClick={() => setShowWaitingListModal(true)}
                  className="  max-md:px-2 h-10 text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl rounded-md transition-all duration-200 flex items-center justify-center space-x-1"
                  size="default"
                >
                  <Rocket className="h-4 w-4" />
                  <span>
                    {getTotalPeopleCount() > 0
                      ? `Send Emails (${getTotalPeopleCount()})`
                      : "Join Waiting List"}
                  </span>
                </Button> */}
              </div>

              <div className="pt-2 max-md:pt-4">
                <p className="flex items-center gap-2 text-[14px] bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent text-left font-semibold">
                  <img src={arrow} alt="Swipe left" className="w-6 h-6 " />
                  <span>Swipe left to see more content</span>
                </p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {/* Empty state or list */}
            {!peopleList || peopleList.length === 0 ? (
              <div className="p-6 sm:p-10 text-center text-gray-700 text-sm w-full max-w-[600px] mx-auto mt-10 overflow-hidden">
                <div className="flex flex-col items-center justify-center overflow-hidden">
                  <svg
                    className="h-14 w-14 text-gray-400 mb-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <p className="font-medium text-base md:text-2xl max-md:text-lg">
                    No peoples data found for this prompt.
                  </p>
                  <p className="text-sm text-gray-400 mt-6">
                    Please try changing the prompts or rewrite your prompt again
                  </p>
                  <p className="text-sm text-gray-400 pt-0 max-md:pt-2">
                    Ex- general managers of hotels, Architects in India
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Scrollable container with header and body */}
                <div className="overflow-auto max-h-[60vh] min-h-[200px]">
                  {/* Header row - now inside scrollable container */}
                  <div className="bg-gray-50/80 backdrop-blur-sm px-3 py-2 border-b border-white/20 min-w-[900px] sticky top-0 z-10">
                    <div className="grid grid-cols-10 gap-6 text-xs font-medium text-gray-700 uppercase tracking-wider">
                      <div className="col-span-2">Name</div>
                      <div className="col-span-2">Job Title</div>
                      <div className="col-span-2">Company</div>
                      <div className="col-span-2">Location</div>
                      <div className="col-span-2">LinkedIn</div>
                    </div>
                  </div>

                  {/* Body rows */}
                  <div className="divide-y divide-gray-200 min-w-[900px]">
                    {displayedPeople.map((person, index) => {
                      const personLastname =
                        person?.last_name_obfuscated?.includes("*")
                          ? ""
                          : person.last_name;
                      // const personLastname =
                      //   person?.last_name_obfuscated?.includes("*")
                      //     ? person.last_name_obfuscated
                      //     : person.last_name;

                      const rawName =
                        person.first_name && personLastname
                          ? `${person.first_name} ${personLastname}`
                          : person.first_name
                            ? person.first_name
                            : person.name ||
                              person.full_name ||
                              person["Full name"] ||
                              "-";

                      const name = toTitleCase(rawName);

                      const linkedin =
                        person.linkedin_url ||
                        person.linkedin ||
                        person["LinkedIn Url"] ||
                        "";

                      const rawTitle =
                        person.title ||
                        person.job_title ||
                        person["Job title"] ||
                        "-";

                      // If the title contains "ceo" (case-insensitive), show "CEO"
                      let title = rawTitle;
                      // if (/ceo/i.test(rawTitle)) {
                      //   title = "CEO";
                      // } else {
                      //   // Trim + Title Case
                      //   title =
                      //     rawTitle.length > 24
                      //       ? toTitleCase(rawTitle.substring(0, 24)) + "..."
                      //       : toTitleCase(rawTitle);
                      // }

                      const rawCompanyName =
                        person.organization?.name ||
                        person.company_name ||
                        person["Company Name"] ||
                        "-";

                      // Trim + Title Case
                      const companyName =
                        rawCompanyName.length > 24
                          ? toTitleCase(rawCompanyName.substring(0, 24)) + "..."
                          : toTitleCase(rawCompanyName);

                      const companyWebsite =
                        person.organization?.website_url || person.domain || "";

                      const geoLeadLabel =
                        person.geo_lead === "IN"
                          ? "India"
                          : person.geo_lead === "US"
                            ? "United States"
                            : person.geo_lead === "UK"
                              ? "United Kingdom"
                              : person.geo_lead === "SG"
                                ? "Singapore"
                                : person.geo_lead === "MY"
                                  ? "Malaysia"
                                  : person.geo_lead === "AE"
                                    ? "United Arab Emirates"
                                    : person.geo_lead === "CA"
                                      ? "Canada"
                                      : person.geo_lead || null;

                      const rawLocation =
                        person.Location1 ||
                        person.Location ||
                        geoLeadLabel ||
                        person.location ||
                        person.Location2 ||
                        [person.country].filter(Boolean).join(", ") ||
                        person.organization?.Location1 ||
                        defaultLocation;

                      // Trim + Title Case
                      const location =
                        rawLocation.length > 10
                          ? toTitleCase(rawLocation.substring(0, 10)) + "..."
                          : toTitleCase(rawLocation);

                      const photo = person?.photo_url
                        ? person?.photo_url
                        : defaultProfile;
                      // defaultProfile

                      return (
                        <div
                          key={person.id || index}
                          className={`px-4 py-3 hover:bg-gray-50 transition-colors ${
                            person.selected
                              ? "bg-blue-50 border-l-4 border-l-blue-500"
                              : ""
                          }`}
                        >
                          <div className="grid grid-cols-10 gap-6 items-center">
                            {/* Name */}
                            <div className="col-span-2 flex items-center space-x-2">
                              {photo ? (
                                <img
                                  src={photo}
                                  alt={name}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <User className="h-6 w-6 text-gray-400" />
                              )}
                              {linkedin ? (
                                <a
                                  href={
                                    linkedin.startsWith("http")
                                      ? linkedin
                                      : `https://${linkedin}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                >
                                  {name}
                                </a>
                              ) : (
                                <span className="text-gray-900 text-sm">
                                  {name}
                                </span>
                              )}
                            </div>

                            {/* Job Title */}
                            <div className="col-span-2 text-sm text-gray-900">
                              {title}
                            </div>

                            {/* Company */}
                            <div className="col-span-2 flex items-center space-x-1">
                              <Building className="h-3 w-3 text-gray-400" />
                              {companyWebsite ? (
                                <a
                                  href={
                                    companyWebsite.startsWith("http")
                                      ? companyWebsite
                                      : `https://${companyWebsite}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                  {companyName}
                                </a>
                              ) : (
                                <span className="text-sm text-blue-600">
                                  {companyName}
                                </span>
                              )}
                            </div>

                            {/* Location */}
                            <div className="col-span-2 flex items-center space-x-1">
                              <MapPin className="h-3 w-3 text-gray-400" />
                              <span className="text-sm text-gray-900">
                                {location}
                              </span>
                            </div>

                            {/* LinkedIn */}
                            <div className="col-span-2">
                              {linkedin ? (
                                <a
                                  href={
                                    linkedin.startsWith("http")
                                      ? linkedin
                                      : `https://${linkedin}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                  {linkedin}
                                </a>
                              ) : (
                                <span className="text-sm text-gray-500">-</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Pagination footer */}
                <div className="flex items-center justify-between px-4 py-3 border-t bg-white text-xs text-gray-700">
                  {/* Left side: Showing X-Y of Z */}
                  <span className="whitespace-nowrap">
                    Showing {totalPeople === 0 ? 0 : peopleStartIndex + 1}–
                    {/* {peopleEndIndex} of {contactsCount} */}
                    {peopleEndIndex} of {totalPeople}
                  </span>

                  {/* Right side: Pagination */}
                  <div className="flex items-center gap-3">
                    {/* Previous Arrow - hidden text, only icon */}
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={peoplePage === 1}
                      onClick={() =>
                        setPeoplePage((prev) => Math.max(1, prev - 1))
                      }
                      className="h-8 w-8 flex items-center justify-center"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    {/* Page indicator */}
                    <span className="text-[10px] sm:text-xs">
                      {peoplePage} / {totalPeoplePages}
                    </span>

                    {/* Next Arrow */}
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={peoplePage === totalPeoplePages}
                      onClick={() =>
                        setPeoplePage((prev) =>
                          Math.min(totalPeoplePages, prev + 1),
                        )
                      }
                      className="h-8 w-8 flex items-center justify-center"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Login Dialog */}
      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Rocket className="h-5 w-5 text-blue-600" />
              <span>Login Required</span>
            </DialogTitle>
            <DialogDescription>
              You need to be logged in to launch a campaign. Please log in to
              continue.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={() => setShowLoginDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleLoginRedirect}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Login
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        defaultTab={authModalTab}
      />
    </div>
  );
};

export default PromptData;
